import keymirror from 'keymirror';

const ANALYTICS_BASE_URL = '/';

export const ACTIVE_CONCEPT_COLORS = {
  Pink: '#ff379c',
  Red: '#ff0032',
  Orange: '#f07600',
  Yellow: '#ffbb2a',
  Green: '#15cf20',
  Blue: '#2563eb',

  Purple: '#7d27bc',
  Grey: '#aaaaaa'
};

export const COLORS = [
  ACTIVE_CONCEPT_COLORS.Blue,
  ACTIVE_CONCEPT_COLORS.Green,
  ACTIVE_CONCEPT_COLORS.Yellow,
  ACTIVE_CONCEPT_COLORS.Orange,
  ACTIVE_CONCEPT_COLORS.Red,
  ACTIVE_CONCEPT_COLORS.Pink,
  ACTIVE_CONCEPT_COLORS.Purple,
  ACTIVE_CONCEPT_COLORS.Grey
];

export const AlertTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

export const DndActions = {
  MOVE: 'move',
  COPY: 'copy',
  NONE: 'none',
  REPLACE: 'replace',
  ADD: 'add'
};

export const ActionTypes = keymirror({
  ACTIVE_CONCEPT_UPDATE: null,
  ACTIVE_CONCEPT_BULK_RECOLOR: null,
  ACTIVE_CONCEPTS_REMOVE: null,
  ACTIVE_CONCEPTS_REPLACE: null,
  ACTIVE_CONCEPTS_ADD: null,
  ACTIVE_CONCEPTS_REORDER: null,
  ACTIVE_LIST_NAME_UPDATE: null,
  ACTIVE_LIST_RESTORE: null,
  CONCEPT_SELECT: null,
  CONCEPT_LIST_OPEN: null,
  FILTER_SELECT: null,
  FILTER_INFO_SPINNER_HIDE: null,
  FILTER_INFO_SPINNER_SHOW: null,
  NOTIFICATION: null,
  PROJECT_EDIT: null,
  PROJECT_EDIT_NEXT_BUILD_LANGUAGE: null,
  PROJECT_EDIT_COLLOCATION: null,
  PROJECT_LOAD: null,
  SHARED_VIEW_DELETE: null,
  SHARED_VIEW_SAVE: null,
  SHARED_VIEWS_GET: null,
  SHARED_CONCEPT_LISTS_LOAD: null,
  SHARED_CONCEPT_LIST_ADD: null,
  SHARED_CONCEPT_LIST_ADD_UNDO: null,
  SHARED_CONCEPT_LIST_SAVE_CONCEPTS: null,
  SHARED_CONCEPT_LIST_SAVE_CONCEPTS_UNDO: null,
  SIDE_PANEL_SELECT_TAB: null,
  WORKSPACES_GET: null,
  UPDATE_FILTER_COUNT: null,
  SELECTED_VIEW_UPDATE: null,
  DOC_SEARCH_RESULTS_LOAD_MORE: null,
  DOC_MARKED_FOR_DELETE: null,
  LOAD_DOCS: null,
  FETCH_DRIVERS_AND_SENTIMENTS: null,
  FETCH_SENTIMENTS: null,
  CANCEL_BUILD_SENTIMENT: null,
  SIDEBAR_COLLAPSE_TOGGLE: null,
  UPDATED_DOCS: null,
  RENAME_METADATA_ITEM: null,
  METADATA_SET: null,
  METADATA_FIELDS_MERGE: null,
  METADATA_TYPE_CONVERT: null
});

export const ErrorTypes = keymirror({
  PROJECT_NOT_READY: null,
  PROJECT_EMPTY: null,
  PROJECT_NEEDS_UPDATING: null,
  UNKNOWN: null
});

export const MetadataTypes = {
  CATEGORY: 'string',
  DATE: 'date',
  NUMBER: 'number',
  SCORE: 'score'
};

export const ConceptTypes = {
  OR: 'or',
  AND: 'and'
};

export const RequestStatuses = keymirror({
  FULFILLED: null,
  PENDING: null,
  REJECTED: null
});

export const RoutePatterns = {
  PROJECT_EXPLORER: '/projects/:workspaceId/:projectId',
  HIGHLIGHTS: '/projects/:workspaceId/:projectId/highlights',
  GALAXY: '/projects/:workspaceId/:projectId/galaxy',
  ONLY_GALAXY: '/projects/:workspaceId/:projectId/only_galaxy',
  VOLUME: '/projects/:workspaceId/:projectId/volume',
  DRIVERS: '/projects/:workspaceId/:projectId/drivers',
  SENTIMENT: '/projects/:workspaceId/:projectId/sentiment',
  CREATE_PROJECT: '/upload',
  UPLOAD_DATA: '/upload/:workspaceId/:projectId',
  UPLOAD_STREAM_DATA: '/upload/stream',
  UPDATE_PROJECT_STREAM_DATA: '/upload/stream-edit/:workspaceId/:projectId',
  SETTINGS: '/settings',
  PROFILE: '/settings/profile',
  PASSWORD: '/settings/password',
  WORKSPACE_SETTINGS: '/settings/workspaces/:workspaceId',
  WORKSPACE_ADD_USER: '/settings/workspaces/:workspaceId/add-user',
  WORKSPACE_USAGE: '/settings/usage/:workspaceId/:billingPeriodIndex?',
  ORGANIZATION_SETTINGS: '/settings/organizations/:organizationId',
  ORGANIZATION_CREATE_WORKSPACE:
    '/settings/organizations/:organizationId/create-workspace',
  ORGANIZATION_USAGE:
    '/settings/organizations/:organizationId/usage/:billingPeriodIndex?',
  TOKENS: '/settings/tokens',
  NEW_TOKEN: '/settings/tokens/new',
  PROJECT_MANAGEMENT: '/projects/:workspaceId/:projectId/manage',
  DASHBOARD_EMBED: '/projects/:workspaceId/:projectId/dashboard',
  DASHBOARD: 'https://dashboards.luminoso.com/app'
};

export const URLs = {
  ANALYTICS_BASE: ANALYTICS_BASE_URL,
  ANALYTICS_LOGIN: `${ANALYTICS_BASE_URL}login.html`,
  DATASET_UPLOAD_HELP:
    'https://www.luminoso.com/getting-started-with-daylight/uploading-a-dataset'
};

export const REACT_QUERY_CLIENT_CONFIG = {
  defaultOptions: {
    queries: {
      cacheTime: Infinity,
      // Treat cached data as fresh for 5 minutes so remounting a screen reuses
      // the cache instead of refetching the same endpoint. (`Math.Infinity`
      // used to be `undefined`, which fell back to the 0ms default and made
      // every query stale immediately.)
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Don't retry requests that throw errors. For now, our API framework is
      // handling failing requests (e.g. 429 errors).
      retry: false
    }
  }
};

export const WORD_COUNT = {
  MIN_SHORT_RESPONSE: 70,
  MIN_LONG_RESPONSE: 300
};

export const UPLOAD_DATA_NUMBER_UNWANTED_CHARACTERS = ['%'];

export const AI_CHAT_LAYOUT = {
  COMMON: {
    X_GAP: 100,
    Y_GAP: 125,
    DEFAULT_WIDTH: 575,
    DEFAULT_HEIGHT: 700,
    MIN_WIDTH: 300,
    MIN_HEIGHT: 590
  },
  AGENTIC: {
    KEY: 'agentic-layout'
  },
  ASSISTANT: {
    KEY: 'assistant-layout',
    EXPANDED_GAP: 250,
    EXPANDED_MIN_WIDTH: 590
  }
};

export const CONVERSION_TABLE = {
  string: {
    number: {
      convert: value => !isNaN(Number(value)),
      key: 'number'
    },
    score: {
      convert: value => !isNaN(Number(value)),
      key: 'score'
    }
  },
  number: {
    string: {
      convert: () => true,
      key: 'string'
    },
    score: {
      convert: () => true,
      key: 'score'
    }
  },
  score: {
    string: {
      convert: () => true,
      key: 'string'
    },
    number: {
      convert: () => true,
      key: 'number'
    }
  },
  date: {
    string: {
      convert: () => true,
      key: 'string'
    }
  }
};
